/**
 * Motivational answer rules — the SINGLE source of truth.
 *
 * There used to be three rule sets for one question:
 *   • journey-web's `validateAnswer` (min 50 chars, min 20 words, PT stoplist);
 *   • a hand-inlined copy of the same rules in business-web's operator screen;
 *   • an unrelated third set in this very file (min 10 chars, 5 required
 *     questions) that was exported from the barrel and imported by nobody.
 * Three answers to "is this answer good enough", none of which had to agree.
 *
 * ── The rule set, as ruled on 2026-09-10 ────────────────────────────────
 *
 * R1  Required, non-empty ...... KEPT as a capability. In practice it never
 *     fires, because R6 makes every question optional. It stays because a
 *     future question set may legitimately mark one required, and a rule that
 *     exists is cheaper than a rule reintroduced under pressure.
 *
 * R2 + R4  Merged into ONE advisory quality rule. 50 characters and 20 words
 *     measure the same thing, and 20 words is nearly always more than 50
 *     characters — so R2 only ever fired on its way to R4. Since validation
 *     returns on the FIRST match, a citizen got staircase feedback: fix the
 *     character count, then be told about the word count. One rule, one
 *     message, never blocking.
 *
 * R3  Ceiling KEPT, derivation FIXED. The old limit was `max(token) × 4` with
 *     the comment "Token ~= 4 characters" — a user-facing character limit
 *     silently derived from an AI context-window budget. Two problems: the
 *     citizen saw an arbitrary number nobody could explain, and the 4-chars-
 *     per-token ratio is English-centric, so a Portuguese author got LESS room
 *     for the same budget. And this rule BLOCKS, so it was a latent second
 *     hard-stop: write a thoughtful long answer, be refused. The ceiling is now
 *     an explicit, generous, locale-neutral constant.
 *
 * R5  English-only PT stoplist ... DELETED, not repaired.
 *     A bag of Portuguese function words — `a o as os no do de em se um mais
 *     como` — every one of which is also a valid English word, `\b`-bounded,
 *     one hit blocks. It measured the wrong thing in the wrong place: English
 *     is already measured by the language test and by MESH's E pillar, so
 *     forcing English into the MOTIVATION free-text contaminates the M signal
 *     (people express motivation worse in a second language) and double-counts
 *     English across two pillars. It also contradicted a shipped capability —
 *     the backend stores `sourceLocale` on every free-text row precisely so the
 *     iQulture calculation can translate for scoring while the stored text
 *     stays as authored. And the cost was wildly asymmetric: a false block
 *     hard-stops a citizen mid-assessment with no override; a false pass costs
 *     nothing. The technique cannot be made correct — short-text language ID is
 *     hard, and function words are the MOST collided vocabulary between the two
 *     languages. If an English signal is wanted later, the honest form is a
 *     non-blocking hint, never a gate.
 *
 * R6  Every question optional (see `motivationalQuestionRequired`).
 * R7  The backend `answer.min(1)` is dropped — the API accepts empty strings.
 *
 * ── What R5 + R6 + R7 add up to, and how it is answered ─────────────────
 *
 * Taken together, NOTHING requires a motivational answer to contain anything.
 * The completeness flag does not catch it either: `motivationalAnswersCompleted`
 * is set from the CLIENT's own progress report, not from the backend inspecting
 * the answers — so an empty submission marks the step complete and the M pillar
 * is scored on no input. That is not an error; it is an iQulture score that
 * looks normal and is partly meaningless.
 *
 * The answer is NOT a rule the citizen can hit. It is `assessMotivationalSignal`
 * below: the scoring reports low confidence when the text carries no signal, and
 * the results view says so. The citizen is never blocked; the score never lies.
 */

import type { MotivationalValidationResult } from './types';

export type { MotivationalValidationResult };

// ── Constants ───────────────────────────────────────────────────────────

/**
 * Advisory quality threshold, in words (merged R2 + R4).
 *
 * Words, not characters: a word count is the thing a person can actually act on
 * ("write a couple more sentences"), and it does not punish languages that spell
 * the same idea with longer words.
 */
export const MOTIVATIONAL_MIN_WORDS = 20;

/**
 * Hard ceiling, in characters (R3). Explicit and generous rather than derived.
 *
 * 4000 characters is roughly 700 words — far more than any of these questions
 * invites, and comfortably inside the model context the answers are scored in
 * even at Portuguese's worse tokenization ratio. It exists so a paste of an
 * entire CV cannot blow the scoring budget, NOT to shape how much a citizen
 * writes. Anything that would make a thoughtful answer bounce is set wrong.
 */
export const MOTIVATIONAL_MAX_CHARS = 4000;

/**
 * R6 — every motivational question is optional.
 *
 * Exposed as a function rather than a bare `false` so the one place that decides
 * this is greppable, and so a future per-question rule has somewhere to live
 * that is not "edit twelve call sites".
 */
export function motivationalQuestionRequired(): boolean {
  return false;
}

// ── Per-answer validation ───────────────────────────────────────────────

/** Machine code for a validation outcome — the caller maps it to localized copy. */
export type MotivationalAnswerCode = 'REQUIRED' | 'TOO_LONG' | 'THIN' | 'OK';

export interface MotivationalAnswerResult {
  /** Only `ERROR` blocks. `WARNING` is advisory and must never gate navigation. */
  status: 'OK' | 'WARNING' | 'ERROR';
  code: MotivationalAnswerCode;
  data?: { wordCount?: number; maxChars?: number };
}

/** Count words in a string. Whitespace-delimited; no language assumptions. */
export function countWords(text: string): number {
  return text
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0).length;
}

/**
 * Validate one motivational answer.
 *
 * Returns `undefined` for an empty answer to an optional question — which, with
 * R6, is every question. An untouched field is not a mistake and must not be
 * decorated as one.
 */
export function validateMotivationalAnswerText(
  answer: string,
  options: { required?: boolean; maxChars?: number } = {}
): MotivationalAnswerResult | undefined {
  const { required = motivationalQuestionRequired(), maxChars = MOTIVATIONAL_MAX_CHARS } = options;
  const trimmed = answer.trim();

  // R1 — required, non-empty.
  if (required && !trimmed) return { status: 'ERROR', code: 'REQUIRED' };

  // Empty + optional: say nothing.
  if (!trimmed) return undefined;

  // R3 — the ceiling. The ONLY blocking rule that a real answer can reach.
  if (trimmed.length > maxChars) {
    return { status: 'ERROR', code: 'TOO_LONG', data: { maxChars } };
  }

  // R2 + R4 — one advisory quality rule, never blocking.
  const wordCount = countWords(trimmed);
  if (wordCount < MOTIVATIONAL_MIN_WORDS) {
    return { status: 'WARNING', code: 'THIN', data: { wordCount } };
  }

  return { status: 'OK', code: 'OK' };
}

// ── Signal assessment (the honest half of R6 + R7) ──────────────────────

/**
 * How much signal a whole set of motivational answers actually carries.
 *
 * `none`   — nothing usable was written. The M pillar must NOT be reported as a
 *            normal score; it is unscored.
 * `low`    — something was written, but too little to distinguish one citizen
 *            from another. Score it, and say the confidence is low.
 * `normal` — enough text to score in the ordinary way.
 */
export type MotivationalSignal = 'none' | 'low' | 'normal';

export interface MotivationalSignalAssessment {
  signal: MotivationalSignal;
  /** Answers with at least one word. */
  answeredCount: number;
  /** Total answers submitted, including empty ones. */
  totalCount: number;
  /** Words across every answer. */
  totalWords: number;
}

/**
 * Assess the signal in a submitted answer set, on the BACKEND, from the answers
 * themselves — never from the client's progress report.
 *
 * This is deliberately not a gate: it changes what the score CLAIMS, not what
 * the citizen is allowed to submit. Thresholds are intentionally forgiving; the
 * question being answered is "could this text distinguish this person at all?",
 * not "is this a good answer?".
 */
export function assessMotivationalSignal(
  answers: Array<{ answer: unknown }>
): MotivationalSignalAssessment {
  // Free-text and non-text answers are counted separately on purpose. The
  // judgement below is about FREE TEXT; a slider carries its own signal and has
  // no word count, so folding it into the same tally would either inflate a
  // silent set or condemn a complete slider-only one.
  let textAnswered = 0;
  let textTotal = 0;
  let nonTextAnswered = 0;
  let totalWords = 0;

  for (const { answer } of answers) {
    if (typeof answer !== 'string') {
      nonTextAnswered++;
      continue;
    }
    textTotal++;
    const words = countWords(answer);
    if (words === 0) continue;
    textAnswered++;
    totalWords += words;
  }

  const answeredCount = textAnswered + nonTextAnswered;
  const base = { answeredCount, totalCount: answers.length, totalWords };

  // Nothing was submitted, or everything submitted was blank.
  if (answers.length === 0 || answeredCount === 0) return { signal: 'none', ...base };

  // No free-text questions in this set at all — there is nothing here to be
  // low-confidence ABOUT, so this function has no opinion.
  if (textTotal === 0) return { signal: 'normal', ...base };

  // Free text was asked for and none of it was written.
  if (textAnswered === 0) return { signal: 'none', ...base };

  // Below one question's worth of advisory-quality text across the WHOLE set,
  // or fewer than half the free-text questions touched: scoreable, but not
  // something to state a confident number about.
  const signal: MotivationalSignal =
    totalWords < MOTIVATIONAL_MIN_WORDS || textAnswered < Math.ceil(textTotal / 2)
      ? 'low'
      : 'normal';

  return { signal, ...base };
}
